import React from "react";

import Topbar from "../components/topbar";
import { useListSearch, ListTitle } from "../components/ui/list-search";
import XeroSyncButton from "../components/xero-sync-button";
import AddClientModal from "../components/popup/add-client";
import AddBtn from "../components/ui/add-btn";
import DeletePopup from "../components/popup/delete";
import DeletePopupMobile from "../components/popup/mobile/delete-mobile";
import clientImage from '../files/images/staff.png'; // Using staff image for clients
import DynamicTableVirtual from "../components/table-virtual";
import { useMediaQuery, useTheme, Button, Box, Fab, IconButton, Menu as MuiMenu, MenuItem, Typography } from "@mui/material";
import { Menu, Plus, Settings, TrendingUp, TrendingDown, Minus } from "lucide-react";
import HoverTooltip from "../components/ui/tooltip";
import ClientInvoicePanel, { ClientInvoiceStats, summarizeClientInvoices, combineClientStats, invoiceTrend, InvoiceTrend } from "../components/client-invoice-panel";import { useSettings } from "../providers/settings-provider";
import SidebarMobileDrawer from "../components/sidebar-mobile-drawer";
import { useSidebarMobile } from "../providers/sidebar-mobile-context";
import useApi from "../hooks/use-api";
import buildApiRequestConfig from "../api/url-builder";
import routes from "../api/routes";
import { useUser } from "../providers/user-provider";
import { useToast } from "../providers/toaster-provider";
import { usePermissions } from "../providers/permissions-provider";
import type { Client as ClientType } from "../components/popup/add-client";

// The $ Last 12 Months figure's colour: the year's trend (blue up, red down).
const TREND_COLORS: Record<InvoiceTrend['direction'], string> = { up: '#1e6fd9', down: '#d32f2f', flat: 'var(--color-default-text)' };

// The 12 months the figure covers - the last 12 complete months before asOf
// (a Xero company's last invoice sync, else today).
const last12RangeLabel = (asOf?: Date | null) => {
    const now = asOf ?? new Date();
    const end = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const start = new Date(now.getFullYear(), now.getMonth() - 12, 1);
    const fmt = (d: Date) => d.toLocaleString('en-AU', { month: 'short', year: 'numeric' });
    return `${fmt(start)} – ${fmt(end)}`;
};

// Hover card for the $ Last 12 Months figure.
// The 12 complete months the trend line is fitted to (invoiceTrend) - up to
// last month, oldest first.
const last12Months = (stats?: ClientInvoiceStats, asOf?: Date | null) => {
    const byPeriod = new Map((stats?.periods ?? []).map(p => [p.period, p.totalAmount]));
    const d = new Date((asOf ?? new Date()).getTime());
    d.setDate(1);
    d.setMonth(d.getMonth() - 12);
    return Array.from({ length: 12 }, (_, x) => {
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        const row = { x, label: d.toLocaleString('en-AU', { month: 'short', year: 'numeric' }), amount: byPeriod.get(key) ?? 0 };
        d.setMonth(d.getMonth() + 1);
        return row;
    });
};

const InvoiceHoverCard: React.FC<{ sum: ReturnType<typeof summarizeClientInvoices>; trend: InvoiceTrend; stats?: ClientInvoiceStats; asOf?: Date | null; money: (n: number) => string }> = ({ sum, trend, stats, asOf, money }) => {
    const invoices = (n: number) => `${n} invoice${n === 1 ? '' : 's'}`;
    const TrendIcon = trend.direction === 'up' ? TrendingUp : trend.direction === 'down' ? TrendingDown : Minus;
    const color = TREND_COLORS[trend.direction];
    const trendRows: [string, number][] = [['Monthly', trend.slope], ['Quarterly', trend.slope * 3], ['Yearly', trend.slope * 12]];
    // Each change as a share of the average month over the last 12 months.
    // The trend's own 12 complete months.
    const months = last12Months(stats, asOf);
    const trendTotal = months.reduce((total, m) => total + m.amount, 0);
    const averageMonth = trendTotal / 12;
    const percent = (amount: number) => (averageMonth ? `${amount >= 0 ? '+' : '-'}${Math.abs((amount / averageMonth) * 100).toFixed(1)}%` : '');
    const signed = (n: number) => `${n >= 0 ? '+' : '-'}${money(Math.abs(n))}`;
    const weightedSum = months.reduce((total, m) => total + (m.x - 5.5) * m.amount, 0);
    const formulaLine = { fontSize: 12, fontVariantNumeric: 'tabular-nums' } as const;
    // How each trend row is worked out, with this client's numbers.
    const rowFormula = (label: string, amount: number, multiplier: number) => (
        <Box sx={{ py: 0.25, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{label}</Typography>
            <Typography sx={formulaLine}>
                {multiplier === 1 ? `Monthly change = ${signed(trend.slope)}` : `${signed(trend.slope)} × ${multiplier} months = ${signed(amount)}`}
            </Typography>
            <Typography sx={formulaLine}>
                {signed(amount)} ÷ {money(averageMonth)} average month = {percent(amount)}
            </Typography>
        </Box>
    );
    return (
        <Box sx={{ minWidth: 230, py: 0.5 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: 'var(--color-text-muted)' }}>Last 12 months</Typography>
            <Typography sx={{ fontSize: 18, fontWeight: 800, fontVariantNumeric: 'tabular-nums', lineHeight: 1.3 }}>{money(sum.last12Amount)}</Typography>
            <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{last12RangeLabel(asOf)} · {invoices(sum.last12Count)}</Typography>

            <Box sx={{ borderTop: '1px solid rgba(0,0,0,0.08)', my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2 }}>
                <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>All time</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{money(sum.allAmount)}</Typography>
            </Box>
            <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)', textAlign: 'right' }}>{invoices(sum.allCount)}</Typography>

            {trendTotal > 0 && (
                <>
                    <Box sx={{ borderTop: '1px solid rgba(0,0,0,0.08)', my: 1 }} />
                    <HoverTooltip
                        placement="right"
                        maxWidth={460}
                        message={
                            <Box sx={{ py: 0.25, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                <Typography sx={{ fontSize: 12, fontWeight: 700 }}>How the trend is worked out</Typography>
                                <Typography sx={formulaLine}>A straight line fitted to the last 12 monthly totals (least squares). Months are numbered 0 to 11; the middle is 5.5.</Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto auto auto', columnGap: 1.5, rowGap: 0.1, fontSize: 11.5, fontVariantNumeric: 'tabular-nums' }}>
                                    {months.map(m => (
                                        <React.Fragment key={m.x}>
                                            <Box sx={{ color: 'var(--color-text-muted)' }}>{m.x} · {m.label}</Box>
                                            <Box sx={{ textAlign: 'right', fontWeight: m.amount ? 700 : 400, color: m.amount ? 'inherit' : 'var(--color-text-muted)' }}>{money(m.amount)}</Box>
                                        </React.Fragment>
                                    ))}
                                </Box>
                                <Box sx={{ borderTop: '1px solid rgba(0,0,0,0.08)' }} />
                                <Typography sx={formulaLine}><b>Average month</b> = {money(trendTotal)} ÷ 12 = {money(averageMonth)}</Typography>
                                <Typography sx={formulaLine}><b>Monthly change</b> = Σ (month − 5.5) × total ÷ Σ (month − 5.5)²</Typography>
                                {months.filter(m => m.amount).map(m => (
                                    <Typography key={m.x} sx={{ ...formulaLine, pl: 1.5, color: 'var(--color-text-muted)' }}>
                                        {m.label}: ({m.x} − 5.5) × {money(m.amount)} = {signed((m.x - 5.5) * m.amount)}
                                    </Typography>
                                ))}
                                <Typography sx={{ ...formulaLine, pl: 1.5 }}>
                                    {signed(weightedSum)} ÷ 143 = <b>{signed(trend.slope)}</b> a month
                                </Typography>
                                <Typography sx={{ ...formulaLine, color: 'var(--color-text-muted)' }}>Flat when the change over the year is under 5% of the average month.</Typography>
                            </Box>
                        }
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5, cursor: 'help' }}>
                            <TrendIcon size={14} color={color} />
                            <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: 'var(--color-text-muted)', borderBottom: '1px dotted var(--color-text-muted)' }}>
                                Trend{trend.direction === 'flat' ? ' · Flat' : ''}
                            </Typography>
                        </Box>
                    </HoverTooltip>
                    {trend.direction !== 'flat' && trendRows.map(([label, amount], i) => (
                        <HoverTooltip key={label} placement="right" message={rowFormula(label, amount, [1, 3, 12][i])}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2, cursor: 'help', borderRadius: 1, '&:hover': { background: 'rgba(0,0,0,0.04)' } }}>
                            <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{label}</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color }}>
                                    {amount >= 0 ? '+' : '-'}{money(Math.abs(amount))}
                                </Typography>
                                <Typography sx={{ fontSize: 11.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color, opacity: 0.75, minWidth: 52, textAlign: 'right' }}>
                                    {percent(amount)}
                                </Typography>
                            </Box>
                        </Box>
                        </HoverTooltip>
                    ))}
                </>
            )}
        </Box>
    );
};

type ClientTableRow = {
    id: number;
    abbr: string;
    company: string;
    recipient: string;
    address: string;
    phone: string;
    email: string;
}

function formatAddress(client: { address1?: string | null; address2?: string | null; suburb?: string | null; state?: string | null; postalCode?: string | null; country?: string | null; }): string {
    const line1 = [client.address1, client.address2].filter(Boolean).join(' ');
    const line2 = [client.suburb, client.state, client.postalCode].filter(Boolean).join(' ');
    const parts = [line1, line2, client.country].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : 'N/A';
}

const Clients: React.FC = () => {
    const { user } = useUser();
    const { can } = usePermissions();
    const canCreate = can('clients', 'create');
    const canUpdate = can('clients', 'update');
    const canDelete = can('clients', 'delete');
    const { showError, showSuccess } = useToast();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const DeleteModal = isMobile ? DeletePopupMobile : DeletePopup;
    const { sidebarOpen, setSidebarOpen } = useSidebarMobile();
    const [openModal, setOpenModal] = React.useState<string>('');
    const [selectedClient, setSelectedClient] = React.useState<ClientTableRow | null>(null);
    const [data, setData] = React.useState<ClientTableRow[]>([]);
    const list = useListSearch(data);
    const { formatAmount } = useSettings();
    // Invoice count/total per client, overall and per month (client-controller.js
    // getClientInvoiceStats), keyed by client id - shown in each row's expanded
    // panel, and only for users who can see invoices.
    const canSeeInvoices = can('invoices', 'read');
    const [invoiceStats, setInvoiceStats] = React.useState<Record<number, ClientInvoiceStats>>({});
    const [fetchInvoiceStats, setFetchInvoiceStats] = useApi();
    // What the 12-month window counts back from - a Xero company's last
    // invoice sync, else today (null).
    const [fetchInvoiceWindow, setFetchInvoiceWindow] = useApi<{ success: boolean; asOf: string | null }>();
    const windowAsOf = React.useMemo(() => (fetchInvoiceWindow.data?.asOf ? new Date(fetchInvoiceWindow.data.asOf) : null), [fetchInvoiceWindow.data]);
    const [sortBy, setSortBy] = React.useState<string>('');
    const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>('asc');

    // API hooks
    const [fetchClients, setFetchClients] = useApi();
    const [createClient, setCreateClient] = useApi();
    const [updateClient, setUpdateClient] = useApi();
    const [deleteClient, setDeleteClient] = useApi();
    const [analyzeAll, setAnalyzeAll] = useApi();
    const [settingsMenuAnchor, setSettingsMenuAnchor] = React.useState<null | HTMLElement>(null);

    React.useEffect(() => {
        document.title = "FineAnts | Clients";
        if (user?.entityId) {
            const params = [
                { param: 'entityId', value: user.entityId },
                { param: 'sortBy', value: sortBy },
                { param: 'sortDirection', value: sortDirection },
            ];
            const { url, method } = buildApiRequestConfig(routes.client.fetchByEntity, params);
            setFetchClients({ url, method });
        }
    }, [user, createClient, updateClient, deleteClient, analyzeAll, sortBy, sortDirection]);

    React.useEffect(() => {
        if (!user?.entityId || !canSeeInvoices) return;
        const { url, method } = buildApiRequestConfig(routes.client.invoiceStats, [{ param: 'entityId', value: user.entityId }]);
        setFetchInvoiceStats({ url, method });
        setFetchInvoiceWindow(buildApiRequestConfig(routes.client.invoiceWindow, [{ param: 'entityId', value: user.entityId }]));
    }, [user, canSeeInvoices, createClient, updateClient, setFetchInvoiceStats, setFetchInvoiceWindow]);

    React.useEffect(() => {
        const { data: statsData, isLoading } = fetchInvoiceStats;
        if (statsData && !isLoading) {
            setInvoiceStats(Object.fromEntries(statsData.map((r: ClientInvoiceStats & { clientId: number }) => [r.clientId, r])));
        }
    }, [fetchInvoiceStats]);

    React.useEffect(() => {
        const { data: clientsData, isLoading, error } = fetchClients;
        if (clientsData && !isLoading) {
            // Transform API data to table format
            const transformedData: ClientTableRow[] = clientsData.map((client: any) => ({
                id: client.id,
                abbr: client.abbr || 'N/A',
                company: client.companyName,
                recipient: client.recipient,
                address: formatAddress(client),
                phone: client.phone || 'N/A',
                email: client.email || 'N/A',
                fax: client.fax || 'N/A',
            }));
            setData(transformedData);
        }
        if (error) {
            showError("Failed to fetch clients");
        }
    }, [fetchClients, showError, setData]);

    React.useEffect(() => {
        const { data, isLoading, error } = deleteClient;
        if (data && !isLoading) {
            showSuccess("Company deleted successfully");
            setOpenModal('');
        }
        if (error) {
            showError(typeof error === 'string' ? error : "Failed to delete company");
        }
    }, [deleteClient]);

    React.useEffect(() => {
        const { data, isLoading, error } = analyzeAll;
        if (data && !isLoading) {
            const skipped = data.skippedHasValue + data.skippedNoMatch;
            if (data.updated > 0) {
                showSuccess(`Suggested a bank description for ${data.updated} of ${data.total} client${data.total === 1 ? '' : 's'}${skipped > 0 ? ` (${skipped} skipped - already set or no paid transactions found)` : ''}`);
            } else {
                showError(`Nothing to suggest for any of the ${data.total} client${data.total === 1 ? '' : 's'} - already set, or no paid transactions found`);
            }
        }
        if (error) {
            showError("Failed to analyze bank descriptions");
        }
    }, [analyzeAll]);

    const handleAnalyzeAll = () => {
        if (!user?.entityId) return;
        const { url, method } = buildApiRequestConfig(routes.client.analyzeAllSearchStrings, [{ param: 'entityId', value: user.entityId }]);
        setAnalyzeAll({ url, method, body: {} });
    };

    React.useEffect(() => {
        const { data, isLoading, error } = createClient;
        if (data && !isLoading) {
            showSuccess("Company added successfully");
            setOpenModal('');
        }
        if (error) {
            showError(typeof error === 'string' ? error : "Failed to create company");
        }
    }, [createClient]);

    React.useEffect(() => {
        const { data, isLoading, error } = updateClient;
        if (data && !isLoading) {
            showSuccess("Company updated successfully");
            setOpenModal('');
        }
        if (error) {
            showError(typeof error === 'string' ? error : "Failed to update company");
        }
    }, [updateClient]);

    const handleEdit = (row: Record<string, any>) => {
        setSelectedClient(row as ClientTableRow);
        setOpenModal('edit');
    };

    const handleDelete = (row: Record<string, any>) => {
        setSelectedClient(row as ClientTableRow);
        setOpenModal('delete');
    };

    const handleConfirmDelete = () => {
        if (selectedClient) {
            const { url, method } = buildApiRequestConfig(routes.client.delete, [{ param: 'id', value: selectedClient.id }]);
            setDeleteClient({ url, method });
        }
    };

    const handleCreateClient = (clientData: ClientType) => {
        const { url, method } = buildApiRequestConfig(routes.client.create);
        const body = {
            entityId: user?.entityId,
            abbr: clientData.companyAbbreviation,
            companyName: clientData.company,
            recipient: clientData.recipient,
            address1: clientData.streetAddress1,
            address2: clientData.streetAddress2,
            state: clientData.state,
            postalCode: clientData.postalCode,
            country: clientData.country,
            suburb: clientData.suburb,
            phone: clientData.phone,
            fax: clientData.fax,
            email: clientData.email,
            nextInvoiceNumber: clientData.nextInvoiceNumber,
            provisionDateOffset: clientData.provisionDateOffset,
            isProvisionDateManual: clientData.isProvisionDateManual,
            searchString: clientData.searchString,
        }
        setCreateClient({ url, method, body });
    };

    const handleEditClient = (clientData: ClientType) => {
        if (!selectedClient) return;

        const { url, method } = buildApiRequestConfig(routes.client.update, [{ param: 'id', value: selectedClient?.id }]);
        const body = {
            abbr: clientData.companyAbbreviation,
            companyName: clientData.company,
            recipient: clientData.recipient,
            address1: clientData.streetAddress1,
            address2: clientData.streetAddress2,
            suburb: clientData.suburb,
            state: clientData.state,
            postalCode: clientData.postalCode,
            country: clientData.country,
            phone: clientData.phone,
            fax: clientData.fax,
            email: clientData.email,
            nextInvoiceNumber: clientData.nextInvoiceNumber,
            provisionDateOffset: clientData.provisionDateOffset,
            isProvisionDateManual: clientData.isProvisionDateManual,
            searchString: clientData.searchString,
        }
        setUpdateClient({ url, method, body });
    };

    const money = (n: number) => (formatAmount ? formatAmount(n) : n.toFixed(2));

    // Total row pinned above the clients - every listed client's invoices
    // combined (so it narrows with the search), with the same chart when expanded.
    const TOTAL_ROW_ID = -1;
    const totalStats = React.useMemo(() => combineClientStats(list.filtered.map(c => invoiceStats[c.id])), [list.filtered, invoiceStats]);
    const tableRows = React.useMemo(() => (canSeeInvoices && Object.keys(invoiceStats).length
        ? [{ id: TOTAL_ROW_ID, isTotal: true, abbr: '', company: 'Total', recipient: '', address: '', phone: '', email: '', fax: '' } as any, ...list.filtered]
        : list.filtered), [canSeeInvoices, invoiceStats, list.filtered]); // eslint-disable-line react-hooks/exhaustive-deps
    const statsFor = (row: Record<string, any>) => (row.id === TOTAL_ROW_ID ? totalStats : invoiceStats[row.id]);

    // Dollars invoiced in the last 12 months; the rest on hover.
    const invoicedColumn = {
        id: 'invoiced12m',
        label: '$ Last 12 Months',
        size: 145,
        type: 'custom' as const,
        mobilePrimary: true,
        mobileOrder: 0,
        mobileWidth: 95,
        customElement: (_value: any, row: Record<string, any>) => {
            const sum = summarizeClientInvoices(statsFor(row), windowAsOf);
            const trend = invoiceTrend(statsFor(row), windowAsOf);
            return (
                <HoverTooltip message={<InvoiceHoverCard sum={sum} trend={trend} stats={statsFor(row)} asOf={windowAsOf} money={money} />} maxWidth={320}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: !sum.last12Amount ? 'var(--color-text-muted)' : TREND_COLORS[trend.direction] }}>
                        {money(sum.last12Amount)}
                    </Typography>
                </HoverTooltip>
            );
        },
    };

    const columns = [
        ...(canSeeInvoices ? [invoicedColumn] : []),
        { 
            id: 'abbr', 
            label: 'Abbr', 
            size: 70, 
            type: 'string' as const,
            onSort: () => {
                setSortBy('abbr');
                setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
            }
        },
        {
            id: 'company',
            label: 'Company Name',
            size: 170,
            type: 'string' as const,
            mobilePrimary: true,
            mobileOrder: 1,
            mobileWidth: 130,
            mobileHideLabel: true,
            onSort: () => {
                setSortBy('companyName');
                setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
            }
        },
        {
            id: 'recipient',
            label: 'Recipient',
            size: 120,
            type: 'string' as const,
            mobilePrimary: true,
            mobileOrder: 2,
            mobileWidth: 90,
            onSort: () => {
                setSortBy('recipient');
                setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
            }
        },
        { id: 'address', label: 'Address', type: 'string' as const },
        {
            id: 'phone',
            label: 'Phone',
            type: 'string' as const,
            size: 110,
            mobilePrimary: !canSeeInvoices,
            mobileOrder: 3,
            mobileWidth: 95,
            onSort: () => {
                setSortBy('phone');
                setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
            }
        },
        { 
            id: 'fax', 
            label: 'Fax', 
            type: 'string' as const, 
            size: 90,
            onSort: () => {
                setSortBy('fax');
                setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
            }
        },
        { 
            id: 'email', 
            label: 'Email', 
            size: 160, 
            type: 'string' as const,
            onSort: () => {
                setSortBy('email');
                setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
            }
        },
        { 
            id: 'actions', 
            label: 'Actions', 
            size: 90, 
            type: 'actions' as const, 
            canEdit: (row: Record<string, any>) => !row.isTotal,
            canDelete: (row: Record<string, any>) => !row.isTotal,
            onDelete: handleDelete,
            onEdit: handleEdit,
        },
    ];

    return (
        <div style={{ width: '100%' }}>
            <AddClientModal
                open={openModal === 'add' || openModal === 'edit'}
                onClose={() => {
                    setOpenModal('');
                    setSelectedClient(null);
                }}
                onCreate={openModal === 'add' ? handleCreateClient : handleEditClient}
                isEdit={openModal === 'edit' ? { id: selectedClient?.id || null, state: true } : { id: null, state: false }}
            />
            <DeleteModal
                open={openModal === 'delete'}
                message="Delete Client?"
                description="This action will permanently remove the selected client."
                image={{ src: clientImage, width: 150, height: 150 }}
                onCancel={() => {
                    setOpenModal('');
                    setSelectedClient(null);
                }}
                onConfirm={handleConfirmDelete}
            />
            <Topbar
                title={<ListTitle title="Clients" list={list} />}
                isMobile={isMobile}
                mobileMenuButton={
                    isMobile && (
                        <Button 
                            variant="contained" 
                            color="primary" 
                            onClick={() => setSidebarOpen(true)} 
                            sx={{ 
                                color: '#fff', 
                                height: 40,
                                display: 'flex',
                                gap: 1,
                                minWidth: 40,
                                p: 1,
                                backgroundColor: 'var(--color-primary)', 
                                '&:hover': { backgroundColor: 'var(--color-primary)' } 
                            }}
                        >
                            <Menu size={20} color="#fff"/>
                        </Button>
                    )
                }
            >
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: '100%', justifyContent: 'flex-end' }}>
                    {!isMobile && (
                        <>
                            <XeroSyncButton type="clients" />
                            <AddBtn disabled={!canCreate} onClick={() => setOpenModal('add')} label="Add Client" />
                            <IconButton
                                size="small"
                                onClick={(e) => setSettingsMenuAnchor(e.currentTarget)}
                                title="Client tools"
                                sx={{ color: 'var(--color-primary)' }}
                            >
                                <Settings size={18} />
                            </IconButton>
                            <MuiMenu
                                anchorEl={settingsMenuAnchor}
                                open={!!settingsMenuAnchor}
                                onClose={() => setSettingsMenuAnchor(null)}
                            >
                                <MenuItem
                                    disabled={!canUpdate || analyzeAll.isLoading}
                                    onClick={() => { setSettingsMenuAnchor(null); handleAnalyzeAll(); }}
                                    title="Suggest a bank description for every client from their own paid transactions - clients that already have one are left alone"
                                    sx={{ fontSize: 14 }}
                                >
                                    {analyzeAll.isLoading ? 'Analyzing…' : 'Analyze Search Strings'}
                                </MenuItem>
                            </MuiMenu>
                        </>
                    )}
                </Box>
            </Topbar>
            <div style={{ padding: '10px' }}>
                <DynamicTableVirtual
                    columns={columns}
                    data={tableRows}
                    isLoading={fetchClients.isLoading}
                    fitContent
                    scrollMode="page"
                    sortState={{ column: sortBy, direction: sortDirection.toUpperCase() as 'ASC' | 'DESC' }}
                    canEditRows={canUpdate}
                    canDeleteRows={canDelete}
                    renderDetailPanel={canSeeInvoices ? (row) => (
                        <ClientInvoicePanel
                            stats={statsFor(row)}
                            isLoading={fetchInvoiceStats.isLoading}
                            formatAmount={(n) => (formatAmount ? formatAmount(n) : String(n))}
                        />
                    ) : undefined}
                />
            </div>
            <SidebarMobileDrawer
                open={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
            />
            {isMobile && canCreate && (
                <Fab
                    onClick={() => setOpenModal('add')}
                    sx={{
                        position: 'fixed',
                        bottom: 20,
                        right: 20,
                        background: 'var(--color-primary)',
                        color: '#fff',
                        '&:hover': { background: 'var(--color-primary-hover)' },
                        zIndex: 999,
                    }}
                >
                    <Plus size={24} />
                </Fab>
            )}
        </div>
    );
};

export default Clients;